<template>
  <div />
</template>

<script>
/**
 * Vue: Athletes content slider (athletes-content-slider)
 * -----------------------------------------------------------------------------
 * Athletes Content Slider.
 * - Limited interactivity section.
 *
 * @param {String} liquid.blockLength - Section's block length.
 * @param {Boolean} liquid.productVideoContent - Content slider is display PDP
 * video content.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { Splide } from '@splidejs/splide';
import { mapState } from 'pinia';

import { values as breakpoints } from '~/config/breakpoints';
import useIndexStore from '~stores/index';

export default {
  name: 'AthletesContentSlider',

  props: {
    liquid: {
      type: Object,
      default: () => ({
        blockLength: 0,
        productVideoContent: false,
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      activeCarousel: false,
      carousel: null,
      layout: 'default',
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
  },

  watch: {
    /**
     * Watch is mobile update to handle resize.
     */
    async isMobile() {
      if (this.layout !== 'default') {
        return;
      }

      await this.$nextTick();
      this.handleResize();
    },
  },

  mounted() {
    if (this.carousel || this.liquid.blockLength < 1) {
      return;
    }

    const selector = `#vue-element [js-athletes-content-slider="${this.liquid.sectionId}"]`;
    this.layout = document.querySelector(selector)?.dataset.layout;

    if (this.layout === 'default') {
      this.constructCarousel(selector);
    }

    if (!this.carousel || this.layout !== 'default') {
      return;
    }

    if (this.isMobile || this.liquid.blockLength > 4) {
      this.carousel.mount();
      this.activeCarousel = true;
    }
  },

  methods: {
    /**
     * Construct and mount the carousel.
     * @param {HTMLElement} selector - Carousel selector.
     */
    constructCarousel(selector) {
      if (!selector) {
        return;
      }

      this.carousel = new Splide(selector, {
        arrows: false,
        drag: 'free',
        focus: 0,
        gap: 'var(--layout-gutter)',
        keyboard: true,
        paginationKeyboard: true,
        mediaQuery: 'min',
        omitEnd: true,
        padding: {
          left: 'var(--layout-margin)',
          right: 'var(--layout-margin)',
        },
        pagination: false,
        perPage: 1.2,
        breakpoints: {
          [breakpoints.m]: {
            perPage: 2.5,
          },
          [breakpoints.l]: {
            arrows: true,
            drag: false,
            padding: 0,
            perPage: 4,
          },
        },
      });
    },

    /**
     * Handle resize event.
     */
    handleResize() {
      if (this.isMobile || this.liquid.blockLength > 4) {
        if (this.activeCarousel) {
          return;
        }

        this.carousel.mount();
        this.activeCarousel = true;
        return;
      }

      if (!this.isMobile && this.liquid.blockLength <= 4) {
        this.carousel.destroy();
        this.activeCarousel = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './athletes-content-slider';
</style>
